import { createFileRoute } from "@tanstack/react-router";
import { routeMeta, leafCanonicalLink } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { CollectionFlagship, type FlagshipContent } from "@/components/CollectionFlagship";
import { getCollection } from "@/data/collections";

const collection = getCollection("longhorn-series");
if (!collection) throw new Error("Longhorn collection is required");

const content: FlagshipContent = {
  eyebrow: "Signature Collection · Commercial",
  heroKicker: "Commercial · San Antonio · Austin · Houston · Corpus Christi",
  lifestyleIntro:
    "Retail centers, restaurant rows, entertainment venues, and ground-up commercial builds — commercial-grade shade that shows up to work every day and looks the part on day 3,000.",
  lifestyleBody:
    "The Longhorn Series is Texas Made Shade's commercial division: retail shopping centers, restaurants and storefronts, new construction packages, modernization of dated commercial properties, amusement parks and entertainment venues, and welded aluminum shade structures built for the wind loads, permits, and daily abuse commercial jobs demand. Same crew that builds our residential work, sized up for property managers, developers, GCs, and business owners. Schools, cities, and public agencies are handled by our Republic Series; hotels, resorts, and event venues by the Rio Grande Series.",
  solutionsHeading: "Commercial-grade shade, sign, and structure",
  solutions: [
    { title: "Car Wash & Vacuum Bay Shade", desc: "Plan vacuum-station, detail-area and queue coverage around site access.", to: "/car-wash-shade-sails" },
    { title: "Dealership & Inventory Parking", desc: "Discuss inventory rows, customer parking and vehicle clearances.", to: "/covered-parking" },
    { title: "Commercial Sail & Tension Shade", desc: "Compare fabric shade layouts for outdoor commercial spaces.", to: "/sail-shades" },
    { title: "Contractors & Facility Managers", desc: "Share site plans, bid requirements and the proposed scope for review.", to: "/architects" },
    { title: "School & Campus Shade", desc: "Explore school shade through our Republic Series public-sector work.", to: "/school-shade-structures" },
    { title: "Storefront & Restaurant Awnings", desc: "Welded aluminum awnings with Sunbrella® canvas for retail and F&B.", to: "/commercial-awnings" },
    { title: "Backlit / Illuminated Awnings", desc: "Lighted awnings and canopies that double as signage.", to: "/backlit-awnings" },
    { title: "Restaurant Patios", desc: "Drop curtains, patio covers, and screens for outdoor dining.", to: "/restaurant-awnings" },
    { title: "Retail Shopping Centers", desc: "Multi-tenant awning programs that unify a center's storefronts.", to: "/commercial-awnings" },
    { title: "New Construction Packages", desc: "Shade, canopies, and structures specified into ground-up commercial builds.", to: "/custom-fabrication" },
    { title: "Modernizing Older Properties", desc: "Refresh dated centers, offices, and strip retail with new awnings and canopies.", to: "/recover-awnings" },
    { title: "Amusement & Entertainment Venues", desc: "Sail shades and canopies for parks, attractions, queue lines, and entertainment districts.", to: "/sail-shades" },
    { title: "Covered Parking & Walkways", desc: "Welded aluminum structures for parking, drive-thrus, and walkways.", to: "/covered-parking" },
    { title: "Awning & Canopy Recover", desc: "Re-canvas commercial awnings without replacing the frame.", to: "/recover-awnings" },
  ],
  trustBadges: [
    "Welded aluminum · commercial spec",
    "Sunbrella® canvas",
    "Permit-ready drawings on request",
    "Serving TX property managers, developers & GCs",
  ],
  faqs: [
    {
      q: "Do you provide drawings and specs for permitting?",
      a: "Yes — Longhorn Series projects include drawings and load information suitable for permit submittal on most Texas jurisdictions. Send scope and site info and we'll package what you need.",
    },
    {
      q: "Can you recover our existing awnings instead of replacing them?",
      a: "Most of the time, yes. If the frame is sound we re-canvas in Sunbrella® at a fraction of the replacement cost. That's the Longhorn Series recover service.",
    },
    {
      q: "Do you work with GCs and property managers?",
      a: "Constantly. We handle single storefronts, multi-tenant retail centers, restaurant groups, new construction packages, commercial modernization, and portfolio recover programs across San Antonio, Austin, Houston, and Corpus Christi.",
    },
    {
      q: "Can awnings act as our sign?",
      a: "Yes — backlit and printed awnings are one of the most cost-effective ways to add branded signage to a storefront or restaurant patio.",
    },
  ],
};

export const Route = createFileRoute("/longhorn-series")({
  head: (ctx) => ({
    meta: routeMeta(ctx, {
      title: "Commercial Shade Structures & Awnings — Longhorn Series™ | Texas Made Shade",
      description:
        "Longhorn Series™ commercial shade: car wash canopies, dealership parking, storefront awnings and recovers. Serving San Antonio and Texas from Seguin.",
      keywords:
        "commercial awnings texas, commercial awnings san antonio, commercial awnings austin, retail shopping center awnings, storefront awnings san antonio, restaurant patio awnings austin, backlit awnings, illuminated awning signage, metal standing seam awnings, covered parking structures texas, walkway canopies commercial, new construction awnings texas, commercial property modernization, amusement park shade structures, awning recover commercial",
    }),
    links: leafCanonicalLink(ctx, collection.path),
    scripts: serviceHeadScripts({
      path: collection.path,
      serviceName: "Longhorn Series Commercial Shade",
      serviceType: "Commercial Awnings & Structures",
      description: collection.description,
      faqs: content.faqs,
      breadcrumbName: "Longhorn Series — Commercial",
    }),
  }),
  component: () => <CollectionFlagship collection={collection} content={content} />,
});
